import { useApolloClient } from "@apollo/client";
import { Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useState } from "react";

import { MANIFEST_DOCUMENTS_QUERY } from "./api";

type ReParseDialogProps = {
  row: {
    id: string;
    sourceWasteManifest?: {
      manifestTrackingNumber?: string | null;
      generatorId?: string | null;
    } | null;
  };
  tenantId: string;
  onClose: () => void;
  /**
   * Called when the analyzing dialog's "Review" button is clicked with the
   * resulting documentIds — parent decides what review surface to open.
   */
  onReadyForReview: (documentIds: string[]) => void;
};

export function ReParseDialog({ row, tenantId, onClose, onReadyForReview }: ReParseDialogProps) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const { createDialog } = useMinimizedDialogs();
  const { queueManifestDocumentExtraction, loading: isQueueing } =
    useQueueManifestDocumentExtraction();

  const [mtnOverride, setMtnOverride] = useState(
    row.sourceWasteManifest?.manifestTrackingNumber ?? ""
  );
  const [generatorIdOverride, setGeneratorIdOverride] = useState(
    row.sourceWasteManifest?.generatorId ?? ""
  );

  const handleConfirm = async () => {
    try {
      const jobs = await queueManifestDocumentExtraction({
        tenantId,
        documentIds: [row.id],
        // Empty strings → undefined so the backend treats them as "no override".
        manifestTrackingNumber: mtnOverride.trim() || undefined,
        generatorId: generatorIdOverride.trim() || undefined,
      });
      // We always pass a single documentId; assert that explicitly so a future
      // batch-re-parse caller doesn't silently drop jobs[1+].
      if (jobs.length !== 1) {
        alerts.error(`Re-parse expected 1 job, got ${jobs.length}. Please try again.`);
        return;
      }
      const jobId = jobs[0]?.job.id;
      if (!jobId) {
        // Mutation succeeded but the backend returned no job — surface as an
        // error and keep the dialog open so the user can retry. Closing
        // silently here would look like the click did nothing.
        alerts.error("Re-parse didn't return a job. Please try again.");
        return;
      }
      const dialogId = `manifest-reparse-${jobId}`;
      createDialog({
        id: dialogId,
        dialogType: DialogType.DocumentAIAnalyzing,
        props: {
          open: true,
          jobId,
          dialogId,
          preferOriginalTitle: true,
          wholeBoxClickable: true,
          onReview: (documentIds: string[]) => {
            if (documentIds.length > 0) {
              onReadyForReview(documentIds);
            }
          },
          onComplete: () => {
            client.refetchQueries({ include: [MANIFEST_DOCUMENTS_QUERY] });
          },
        },
      });
      onClose();
    } catch (err) {
      // Keep the dialog open so the user can retry without losing typed overrides.
      alerts.error("Failed to queue re-parsing.", err instanceof Error ? err : undefined);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Re-parse manifest from document"
      maxWidth="sm"
      fullWidth
      primaryActionButtonProps={{
        saveText: "Re-parse",
        onClick: handleConfirm,
        loading: isQueueing,
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Override the values that the AI couldn't parse correctly. Both fields are optional — leave
          a field unchanged to keep the current parsed value, or clear it to let the AI re-parse
          from scratch. Manual edits since the last parse will be lost.
        </Typography>
        <TextField
          label="Tracking Number"
          value={mtnOverride}
          onChange={(e) => setMtnOverride(e.target.value)}
          size="small"
          fullWidth
          helperText="Override the manifest tracking number used during re-parsing."
        />
        <TextField
          label="Generator EPA ID"
          value={generatorIdOverride}
          onChange={(e) => setGeneratorIdOverride(e.target.value)}
          size="small"
          fullWidth
          helperText="Override the generator EPA ID — useful when AI misread the digits."
        />
      </Stack>
    </Dialog>
  );
}
